import React, { createContext, ReactNode, useContext, useReducer } from 'react';

import {
  AlertConfiguration,
  IndicatorWithAlerts,
} from '../../../../../types/indicator-alert-configuration';

// Tipos do contexto
interface AlertContextState {
  percentageAlerts: IndicatorWithAlerts[];
  amountAlerts: IndicatorWithAlerts[];
  toDelete: Array<number>;
  useAlertDefaultData: boolean;
}

type TabType = 'percentage' | 'amount';

// Ações do reducer
type AlertAction =
  | { type: 'SET_PERCENTAGE_ALERTS'; payload: IndicatorWithAlerts[] }
  | { type: 'SET_AMOUNT_ALERTS'; payload: IndicatorWithAlerts[] }
  | {
      type: 'ADD_ALERT';
      payload: {
        tabType: TabType;
        alert: AlertConfiguration;
        indicatorKey: string;
      };
    }
  | {
      type: 'UPDATE_ALERT';
      payload: {
        tabType: TabType;
        alertId: string;
        updates: Partial<AlertConfiguration>;
        indicatorKey: string;
      };
    }
  | {
      type: 'REMOVE_ALERT';
      payload: {
        tabType: TabType;
        alertId: string;
        indicatorKey: string;
        id: number | null;
      };
    }
  | { type: 'CLEAR_ALL_ALERTS' }
  | {
      type: 'INITIALIZE_ALERTS';
      payload: {
        percentage: IndicatorWithAlerts[];
        amount: IndicatorWithAlerts[];
        toDelete: number[];
      };
    }
  | {
      type: 'SET_ALERTS_TO_DELETE';
      payload: number[];
    }
  | {
      type: 'SET_USE_ALERT_DEFAULT_DATA';
      payload: boolean;
    };

// Estado inicial
const initialState: AlertContextState = {
  percentageAlerts: [],
  amountAlerts: [],
  toDelete: [],
  useAlertDefaultData: false,
};

// Reducer para gerenciar estado
function alertReducer(
  state: AlertContextState,
  action: AlertAction,
): AlertContextState {
  switch (action.type) {
    case 'SET_PERCENTAGE_ALERTS':
      return {
        ...state,
        percentageAlerts: action.payload,
      };

    case 'SET_AMOUNT_ALERTS':
      return {
        ...state,
        amountAlerts: action.payload,
      };

    case 'ADD_ALERT': {
      const { tabType, alert, indicatorKey } = action.payload;
      const targetAlerts =
        tabType === 'percentage' ? state.percentageAlerts : state.amountAlerts;

      const updatedAlerts = targetAlerts.map((indicator) => {
        if (indicator.key === indicatorKey) {
          return {
            ...indicator,
            alerts: [...indicator.alerts, alert],
          };
        }
        return indicator;
      });

      return {
        ...state,
        [tabType === 'percentage' ? 'percentageAlerts' : 'amountAlerts']:
          updatedAlerts,
      };
    }

    case 'UPDATE_ALERT': {
      const { tabType, alertId, updates, indicatorKey } = action.payload;
      const targetAlerts =
        tabType === 'percentage' ? state.percentageAlerts : state.amountAlerts;

      const updatedAlerts = targetAlerts.map((indicator) => {
        if (indicator.key === indicatorKey) {
          return {
            ...indicator,
            alerts: indicator.alerts.map((alert) => {
              if (alert.internalId === alertId) {
                return { ...alert, ...updates };
              }
              return alert;
            }),
          };
        }
        return indicator;
      });

      return {
        ...state,
        [tabType === 'percentage' ? 'percentageAlerts' : 'amountAlerts']:
          updatedAlerts,
      };
    }

    case 'REMOVE_ALERT': {
      const { tabType, alertId, indicatorKey, id } = action.payload;
      const targetAlerts =
        tabType === 'percentage' ? state.percentageAlerts : state.amountAlerts;

      if (id !== null) {
        state.toDelete.push(id);
      }

      const updatedAlerts = targetAlerts.map((indicator) => {
        if (indicator.key === indicatorKey) {
          return {
            ...indicator,
            alerts: indicator.alerts.filter(
              (alert) => alert.internalId !== alertId,
            ),
          };
        }
        return indicator;
      });

      return {
        ...state,
        [tabType === 'percentage' ? 'percentageAlerts' : 'amountAlerts']:
          updatedAlerts,
      };
    }

    case 'CLEAR_ALL_ALERTS':
      return initialState;

    case 'INITIALIZE_ALERTS':
      return {
        percentageAlerts: action.payload.percentage,
        amountAlerts: action.payload.amount,
        toDelete: action.payload.toDelete || [],
        useAlertDefaultData: false,
      };

    case 'SET_ALERTS_TO_DELETE':
      return {
        ...state,
        toDelete: action.payload,
      };

    case 'SET_USE_ALERT_DEFAULT_DATA':
      return {
        ...state,
        useAlertDefaultData: action.payload,
      };

    default:
      return state;
  }
}

// Interface do contexto
interface AlertContextType extends AlertContextState {
  // Funções de gerenciamento
  setPercentageAlerts: (alerts: IndicatorWithAlerts[]) => void;
  setAmountAlerts: (alerts: IndicatorWithAlerts[]) => void;

  // Funções de alerta
  addAlert: (
    tabType: TabType,
    alert: AlertConfiguration,
    indicatorKey: string,
  ) => void;
  updateAlert: (
    tabType: TabType,
    alertId: string,
    updates: Partial<AlertConfiguration>,
    indicatorKey: string,
  ) => void;
  removeAlert: (
    tabType: TabType,
    alertId: string,
    indicatorKey: string,
    id: number | null,
  ) => void;

  // Funções utilitárias
  clearAllAlerts: () => void;
  initializeAlerts: (
    percentage: IndicatorWithAlerts[],
    amount: IndicatorWithAlerts[],
    toDelete: number[],
  ) => void;

  // Getters
  getAlertsByTab: (tabType: TabType) => IndicatorWithAlerts[];
  getAlertsByIndicator: (
    tabType: TabType,
    indicatorKey: string,
  ) => AlertConfiguration[];
  getAlertsToDelete: () => number[];
  setAlertsToDelete: (alertsToDelete: number[]) => void;
  setUseAlertDefaultData: (useAlertDefaultData: boolean) => void;
}

// Criar contexto
const AlertContext = createContext<AlertContextType | undefined>(undefined);

// Provider do contexto
interface AlertContextProviderProps {
  children: ReactNode;
}

export function AlertContextProvider({ children }: AlertContextProviderProps) {
  const [state, dispatch] = useReducer(alertReducer, initialState);

  // Funções de gerenciamento
  const setPercentageAlerts = (alerts: IndicatorWithAlerts[]) => {
    dispatch({ type: 'SET_PERCENTAGE_ALERTS', payload: alerts });
  };

  const setAmountAlerts = (alerts: IndicatorWithAlerts[]) => {
    dispatch({ type: 'SET_AMOUNT_ALERTS', payload: alerts });
  };

  // Funções de alerta
  const addAlert = (
    tabType: TabType,
    alert: AlertConfiguration,
    indicatorKey: string,
  ) => {
    dispatch({ type: 'ADD_ALERT', payload: { tabType, alert, indicatorKey } });
  };

  const updateAlert = (
    tabType: TabType,
    alertId: string,
    updates: Partial<AlertConfiguration>,
    indicatorKey: string,
  ) => {
    dispatch({
      type: 'UPDATE_ALERT',
      payload: { tabType, alertId, updates, indicatorKey },
    });
  };

  const removeAlert = (
    tabType: TabType,
    alertId: string,
    indicatorKey: string,
    id: number | null,
  ) => {
    dispatch({
      type: 'REMOVE_ALERT',
      payload: { tabType, alertId, indicatorKey, id },
    });
  };

  // Funções utilitárias
  const clearAllAlerts = () => {
    dispatch({ type: 'CLEAR_ALL_ALERTS' });
  };

  const initializeAlerts = (
    percentage: IndicatorWithAlerts[],
    amount: IndicatorWithAlerts[],
    toDelete: number[] = [],
  ) => {
    dispatch({
      type: 'INITIALIZE_ALERTS',
      payload: { percentage, amount, toDelete },
    });
  };

  // Getters
  const getAlertsByTab = (tabType: TabType): IndicatorWithAlerts[] => {
    return tabType === 'percentage'
      ? state.percentageAlerts
      : state.amountAlerts;
  };

  const getAlertsByIndicator = (
    tabType: TabType,
    indicatorKey: string,
  ): AlertConfiguration[] => {
    const tabAlerts = getAlertsByTab(tabType);
    const indicator = tabAlerts.find((ind) => ind.key === indicatorKey);
    return indicator ? indicator.alerts : [];
  };

  const getAlertsToDelete = () => {
    return state.toDelete;
  };

  const setAlertsToDelete = (alertsToDelete: number[]) => {
    dispatch({ type: 'SET_ALERTS_TO_DELETE', payload: alertsToDelete });
  };

  const setUseAlertDefaultData = (useAlertDefaultData: boolean) => {
    dispatch({
      type: 'SET_USE_ALERT_DEFAULT_DATA',
      payload: useAlertDefaultData,
    });
  };

  const contextValue: AlertContextType = {
    ...state,
    setPercentageAlerts,
    setAmountAlerts,
    addAlert,
    updateAlert,
    removeAlert,
    clearAllAlerts,
    initializeAlerts,
    getAlertsByTab,
    getAlertsByIndicator,
    getAlertsToDelete,
    setAlertsToDelete,
    setUseAlertDefaultData,
  };

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
    </AlertContext.Provider>
  );
}

// Hook personalizado para usar o contexto
export function useAlertContext(): AlertContextType {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error(
      'useAlertContext deve ser usado dentro de um AlertContextProvider',
    );
  }
  return context;
}
